import React, { useState, useRef, useEffect } from 'react'
import { gql } from 'graphql-request'
import { authorizedGraphQLRequest } from '@/api/graphqlClient'
import { X } from 'lucide-react'
import { Button } from '@/components/ui/button'

interface MicrofankProject {
  id: number
  name: string
  description?: string
}

interface MicrofankFlow {
  id: number
  name: string
  projectId: number
}

interface MicrofankPayload {
  id: number
  name: string
  description?: string
  projectId: number
}

interface ModuleConfigFields {
  id?: number
  name: string
  coachFlowId: number
  roleplayFlowId: number
  reviewPromptPayloadId: number
  timerDuration: number
  defaultRemainingTime: number
  reviewPageSize: number
  microfankProjectId?: number | null
}

interface CreateModuleModalProps {
  isOpen: boolean
  onClose: () => void
  onSuccess: () => void
  editModule?: ModuleConfigFields | null
}

const CREATE_MODULE = gql`
  mutation createModule($input: CreateModuleInput!) {
    createModule(input: $input) {
      id
      name
      coachFlowId
      roleplayFlowId
      reviewPromptPayloadId
      timerDuration
      defaultRemainingTime
      reviewPageSize
      microfankProjectId
    }
  }
`

const UPDATE_MODULE = gql`
  mutation updateModule($moduleId: Int!, $input: UpdateModuleInput!) {
    updateModule(moduleId: $moduleId, input: $input) {
      id
      name
      coachFlowId
      roleplayFlowId
      reviewPromptPayloadId
      timerDuration
      defaultRemainingTime
      reviewPageSize
      microfankProjectId
    }
  }
`

const GET_PROJECTS = gql`
  query getProjects {
    getProjects {
      id
      name
      description
    }
  }
`

const GET_FLOWS = gql`
  query getFlows($projectId: Int!) {
    getFlows(projectId: $projectId) {
      id
      name
      projectId
    }
  }
`

const GET_PAYLOADS = gql`
  query getPayloads($projectId: Int!) {
    getPayloads(projectId: $projectId, limit: 100) {
      data {
        id
        name
        description
        projectId
      }
    }
  }
`

const DEFAULTS = {
  timerDuration: 180,
  defaultRemainingTime: 300,
  reviewPageSize: 10,
}

const selectClass =
  'w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent bg-white disabled:bg-gray-50 disabled:text-gray-400 disabled:cursor-not-allowed'

function FallbackOption({ id, list }: { id: number | null; list: { id: number }[] }) {
  if (!id || list.some((item) => item.id === id)) return null
  return <option value={id}>(ID: {id} — not found)</option>
}

const CreateModuleModal: React.FC<CreateModuleModalProps> = ({ isOpen, onClose, onSuccess, editModule }) => {
  const modalRef = useRef<HTMLDivElement>(null)

  // Core fields
  const [name, setName] = useState('')
  const [timerDuration, setTimerDuration] = useState(String(DEFAULTS.timerDuration))
  const [defaultRemainingTime, setDefaultRemainingTime] = useState(String(DEFAULTS.defaultRemainingTime))
  const [reviewPageSize, setReviewPageSize] = useState(String(DEFAULTS.reviewPageSize))

  // Microfrank-linked fields
  const [microfankProjectId, setMicrofankProjectId] = useState<number | null>(null)
  const [coachFlowId, setCoachFlowId] = useState<number | null>(null)
  const [roleplayFlowId, setRoleplayFlowId] = useState<number | null>(null)
  const [reviewPromptPayloadId, setReviewPromptPayloadId] = useState<number | null>(null)

  // Dropdown data
  const [projects, setProjects] = useState<MicrofankProject[]>([])
  const [flows, setFlows] = useState<MicrofankFlow[]>([])
  const [payloads, setPayloads] = useState<MicrofankPayload[]>([])

  // Loading / error states
  const [projectsLoading, setProjectsLoading] = useState(false)
  const [flowsLoading, setFlowsLoading] = useState(false)
  const [projectsError, setProjectsError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const isEditMode = !!editModule

  // Fetch projects when modal opens
  useEffect(() => {
    if (!isOpen) return

    setProjectsLoading(true)
    setProjectsError(null)
    authorizedGraphQLRequest<{ getProjects: MicrofankProject[] }>(GET_PROJECTS)
      .then((res) => setProjects(res.getProjects || []))
      .catch(() => setProjectsError('Failed to load projects'))
      .finally(() => setProjectsLoading(false))
  }, [isOpen])

  // Populate fields from editModule when opening in edit mode
  useEffect(() => {
    if (editModule && isOpen) {
      setName(editModule.name)
      setTimerDuration(String(editModule.timerDuration))
      setDefaultRemainingTime(String(editModule.defaultRemainingTime))
      setReviewPageSize(String(editModule.reviewPageSize))
      setCoachFlowId(editModule.coachFlowId)
      setRoleplayFlowId(editModule.roleplayFlowId)
      setReviewPromptPayloadId(editModule.reviewPromptPayloadId)
      setMicrofankProjectId(editModule.microfankProjectId ?? null)
    }
  }, [editModule, isOpen])

  // Fetch flows + payloads whenever microfankProjectId changes
  useEffect(() => {
    if (!microfankProjectId) {
      setFlows([])
      setPayloads([])
      return
    }

    setFlowsLoading(true)
    Promise.all([
      authorizedGraphQLRequest<{ getFlows: MicrofankFlow[] }>(GET_FLOWS, { projectId: microfankProjectId }),
      authorizedGraphQLRequest<{ getPayloads: { data: MicrofankPayload[] } }>(GET_PAYLOADS, { projectId: microfankProjectId }),
    ])
      .then(([flowsRes, payloadsRes]) => {
        setFlows(flowsRes.getFlows || [])
        setPayloads(payloadsRes.getPayloads?.data || [])
      })
      .catch(() => {
        setFlows([])
        setPayloads([])
      })
      .finally(() => setFlowsLoading(false))
  }, [microfankProjectId])

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && isOpen) handleClose()
    }
    window.addEventListener('keydown', handleKeyDown)
    return () => window.removeEventListener('keydown', handleKeyDown)
  }, [isOpen])

  const handleClose = () => {
    setName('')
    setTimerDuration(String(DEFAULTS.timerDuration))
    setDefaultRemainingTime(String(DEFAULTS.defaultRemainingTime))
    setReviewPageSize(String(DEFAULTS.reviewPageSize))
    setMicrofankProjectId(null)
    setCoachFlowId(null)
    setRoleplayFlowId(null)
    setReviewPromptPayloadId(null)
    setFlows([])
    setPayloads([])
    setProjectsError(null)
    setError(null)
    onClose()
  }

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const val = e.target.value ? Number(e.target.value) : null
    setMicrofankProjectId(val)
    // Reset dependent fields when project changes
    setCoachFlowId(null)
    setRoleplayFlowId(null)
    setReviewPromptPayloadId(null)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    if (!name.trim()) {
      setError('Module name is required')
      return
    }

    try {
      setSubmitting(true)
      setError(null)

      const input: Record<string, any> = {
        name: name.trim(),
        timerDuration: parseInt(timerDuration, 10) || DEFAULTS.timerDuration,
        defaultRemainingTime: parseInt(defaultRemainingTime, 10) || DEFAULTS.defaultRemainingTime,
        reviewPageSize: parseInt(reviewPageSize, 10) || DEFAULTS.reviewPageSize,
        microfankProjectId: microfankProjectId ?? undefined,
      }

      if (coachFlowId != null) input.coachFlowId = coachFlowId
      if (roleplayFlowId != null) input.roleplayFlowId = roleplayFlowId
      if (reviewPromptPayloadId != null) input.reviewPromptPayloadId = reviewPromptPayloadId

      if (isEditMode && editModule?.id) {
        await authorizedGraphQLRequest(UPDATE_MODULE, { moduleId: editModule.id, input })
      } else {
        await authorizedGraphQLRequest(CREATE_MODULE, { input })
      }

      handleClose()
      onSuccess()
    } catch (err: any) {
      console.error(`Failed to ${isEditMode ? 'update' : 'create'} module:`, err)
      const message = err?.response?.errors?.[0]?.message || `Failed to ${isEditMode ? 'update' : 'create'} module`
      setError(message)
    } finally {
      setSubmitting(false)
    }
  }

  const handleOutsideClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (e.target === e.currentTarget) handleClose()
  }

  if (!isOpen) return null

  const dropdownsDisabled = !microfankProjectId || flowsLoading

  return (
    <div className="fixed inset-0 flex items-center justify-center p-4 z-50" onClick={handleOutsideClick}>
      <div className="fixed inset-0 bg-black/40" />

      <div ref={modalRef} className="relative bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto animate-in zoom-in-95 duration-150">
        {/* Header */}
        <div className="p-6 border-b border-gray-200 flex items-center justify-between sticky top-0 bg-white z-10">
          <h2 className="text-xl font-semibold text-gray-800">{isEditMode ? 'Edit Module' : 'Create Module'}</h2>
          <button onClick={handleClose} className="p-1 rounded-full hover:bg-gray-100 transition-colors">
            <X className="h-5 w-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {error && <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>}

          {/* Module Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Module Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter module name"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              required
            />
          </div>

          {/* Microfrank Project */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Microfrank Project</label>
            {projectsError ? (
              <p className="text-sm text-red-600">{projectsError}</p>
            ) : (
              <select
                value={microfankProjectId ?? ''}
                onChange={handleProjectChange}
                disabled={projectsLoading}
                className={selectClass}
              >
                <option value="">{projectsLoading ? 'Loading projects…' : 'Select a project'}</option>
                {projects.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name}
                  </option>
                ))}
              </select>
            )}
          </div>

          {/* Coach Flow */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Coach Flow</label>
            <select
              value={coachFlowId ?? ''}
              onChange={(e) => setCoachFlowId(e.target.value ? Number(e.target.value) : null)}
              disabled={dropdownsDisabled}
              className={selectClass}
            >
              <option value="">{flowsLoading ? 'Loading flows…' : !microfankProjectId ? 'Select a project first' : 'Select a flow'}</option>
              {coachFlowId != null && <FallbackOption id={coachFlowId} list={flows} />}
              {flows.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>

          {/* Roleplay Flow */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Roleplay Flow</label>
            <select
              value={roleplayFlowId ?? ''}
              onChange={(e) => setRoleplayFlowId(e.target.value ? Number(e.target.value) : null)}
              disabled={dropdownsDisabled}
              className={selectClass}
            >
              <option value="">{flowsLoading ? 'Loading flows…' : !microfankProjectId ? 'Select a project first' : 'Select a flow'}</option>
              {roleplayFlowId != null && <FallbackOption id={roleplayFlowId} list={flows} />}
              {flows.map((f) => (
                <option key={f.id} value={f.id}>
                  {f.name}
                </option>
              ))}
            </select>
          </div>

          {/* Review Prompt */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Review Prompt</label>
            <select
              value={reviewPromptPayloadId ?? ''}
              onChange={(e) => setReviewPromptPayloadId(e.target.value ? Number(e.target.value) : null)}
              disabled={dropdownsDisabled}
              className={selectClass}
            >
              <option value="">{flowsLoading ? 'Loading prompts…' : !microfankProjectId ? 'Select a project first' : 'Select a prompt'}</option>
              {reviewPromptPayloadId != null && <FallbackOption id={reviewPromptPayloadId} list={payloads} />}
              {payloads.map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
          </div>

          {/* Numeric config fields */}
          {[
            { label: 'Timer Duration (seconds)', value: timerDuration, setter: setTimerDuration, defaultVal: DEFAULTS.timerDuration },
            { label: 'Default Remaining Time (seconds)', value: defaultRemainingTime, setter: setDefaultRemainingTime, defaultVal: DEFAULTS.defaultRemainingTime },
            { label: 'Review Page Size', value: reviewPageSize, setter: setReviewPageSize, defaultVal: DEFAULTS.reviewPageSize },
          ].map((field) => (
            <div key={field.label}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{field.label}</label>
              <input
                type="number"
                value={field.value}
                onChange={(e) => field.setter(e.target.value)}
                min="0"
                placeholder={String(field.defaultVal)}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent"
              />
              <p className="text-xs text-gray-400 mt-0.5">Default: {field.defaultVal}</p>
            </div>
          ))}

          {/* Actions */}
          <div className="flex justify-end gap-2 pt-2">
            <Button type="button" variant="outline" onClick={handleClose} disabled={submitting}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting || !name.trim()}>
              {submitting ? (isEditMode ? 'Saving...' : 'Creating...') : isEditMode ? 'Save Changes' : 'Create Module'}
            </Button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateModuleModal
